import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { IonSearchbar, ModalController, NavParams } from '@ionic/angular';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';
import { StringUtils } from '@utils/StringUtils';

interface IEntity {
    Id: any;
    Name: string;
    Mono?: string;
}

/**
 * Modal de seleção com pesquisa (single ou multi).
 * Substitui os AlertController de rádios/checkboxes (que não suportam filtro).
 *
 * componentProps:
 *  - entities: IEntity[]            lista de opções ({ Id, Name })
 *  - multiple?: boolean             multi-seleção (default false)
 *  - currentEntityId?: any          pré-seleção (single)
 *  - selectedIds?: any[]            pré-seleção (multi)
 *  - headerTitle?, subtitle?, searchPlaceholder?, confirmText?, nounPlural?
 *  - showClose?: boolean            mostra o X para cancelar (default false)
 *
 * Dismiss: role Confirm com data = entidade (single) ou entidades[] (multi).
 */
@Component({
    selector: 'app-select-project',
    templateUrl: './select-project.component.html',
    styleUrls: ['./select-project.component.scss'],
})
export class SelectProjectModalComponent implements AfterViewInit {

    @ViewChild('search') SearchBar: IonSearchbar;

    public Entities: IEntity[] = [];
    public FilteredEntities: IEntity[] = [];
    public SearchText: string = '';
    public Selected: IEntity[] = [];
    public Multiple: boolean = false;
    public ShowClose: boolean = false;
    public HasPriorSelection: boolean = false;
    public CopiedId: any = null;
    private CurrentEntityId: any = null;

    // cópia configurável (defaults = seleção de projeto)
    public HeaderTitle: string = 'Selecione o projeto';
    public Subtitle: string = 'Escolha o projeto onde quer entrar.';
    public SearchPlaceholder: string = 'Pesquisar projeto';
    public ConfirmText: string = 'Entrar';
    public NounPlural: string = 'projetos';

    constructor(
        private ModalController: ModalController,
        private NavParams: NavParams
    ) {
        const entities: IEntity[] = this.NavParams.get('entities') || [];
        this.Entities = entities.map(e => ({ ...e, Mono: this.Monogram(e?.Name) }));

        this.Multiple = !!this.NavParams.get('multiple');
        this.ShowClose = !!this.NavParams.get('showClose');

        this.HeaderTitle = this.NavParams.get('headerTitle') || this.HeaderTitle;
        this.Subtitle = this.NavParams.get('subtitle') ?? this.Subtitle;
        this.SearchPlaceholder = this.NavParams.get('searchPlaceholder') || this.SearchPlaceholder;
        this.ConfirmText = this.NavParams.get('confirmText') || this.ConfirmText;
        this.NounPlural = this.NavParams.get('nounPlural') || this.NounPlural;

        if (this.Multiple) {
            const ids = (this.NavParams.get('selectedIds') || []).map((x: any) => String(x));
            this.Selected = this.Entities.filter(e => ids.includes(String(e.Id)));
        } else {
            const currentId = this.NavParams.get('currentEntityId');
            this.CurrentEntityId = currentId ?? null;
            if (currentId != null) {
                const found = this.Entities.find(e => String(e.Id) === String(currentId));
                if (found) this.Selected = [found];
            }
        }

        this.HasPriorSelection = this.Selected.length > 0;

        this.FilteredEntities = this.Entities.slice();
    }

    ngAfterViewInit(): void {
        if (this.ShowSearch)
            setTimeout(() => this.SearchBar?.setFocus(), 300);
    }

    get ShowSearch(): boolean {
        return this.Entities.length > 8;
    }

    get TotalCount(): number {
        return this.Entities.length;
    }

    get ShownCount(): number {
        return this.FilteredEntities.length;
    }

    get SelectedCount(): number {
        return this.Selected.length;
    }

    get HasSelection(): boolean {
        if (!this.Multiple && this.IsSameAsCurrent) return false;
        return this.Selected.length > 0;
    }

    get IsSameAsCurrent(): boolean {
        if (this.Multiple || !this.Selected.length) return false;
        return String(this.Selected[0]?.Id) === String(this.CurrentEntityId);
    }

    get IsFiltering(): boolean {
        return !!(this.SearchText || '').trim();
    }

    public OnSearch(ev: any): void {
        this.SearchText = ev?.detail?.value ?? ev?.target?.value ?? '';
        this.ApplyFilter();
    }

    private ApplyFilter(): void {
        const text = (this.SearchText || '').toLowerCase().trim();
        this.FilteredEntities = text
            ? this.Entities.filter(e => (e?.Name || '').toLowerCase().includes(text))
            : this.Entities.slice();
    }

    public IsSelected(item: IEntity): boolean {
        return this.Selected.some(s => String(s?.Id) === String(item?.Id));
    }

    public Toggle(item: IEntity): void {
        if (this.Multiple) {
            this.Selected = this.IsSelected(item)
                ? this.Selected.filter(s => String(s?.Id) !== String(item?.Id))
                : [...this.Selected, item];
        } else {
            this.Selected = [item];
        }
    }

    public trackByFn(index: number, item: IEntity): any {
        return item?.Id ?? index;
    }

    public CopyId(item: IEntity): void {
        if (!StringUtils.ClipboardText(String(item?.Id ?? ''))) return;

        this.CopiedId = item?.Id;
        setTimeout(() => this.CopiedId = null, 1200);
    }

    public Close(): void {
        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Cancel);
    }

    public Confirm(): void {
        if (!this.HasSelection) return;
        const data = this.Multiple ? this.Selected : this.Selected[0];
        this.ModalController.dismiss(data, EnumGenericRoleActionAlerts.Confirm);
    }

    /**
     * Iniciais para a placa de monograma.
     * Remove o prefixo "Câmara Municipal de/da/do" (quando existe) e usa
     * as iniciais das primeiras palavras significativas do que resta.
     */
    private Monogram(name: string): string {
        const source = (name || '').trim();
        if (!source) return '--';

        const stripped = source.replace(/^c[âa]mara\s+municipal\s+d[eao]s?\s+/i, '').trim();
        const base = stripped || source;

        const words = base
            .split(/\s+/)
            .filter(w => !/^(de|da|do|dos|das|e)$/i.test(w));

        if (words.length >= 2)
            return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();

        return base.replace(/\s+/g, '').substring(0, 2).toUpperCase();
    }
}
